'use client';
import React, { useEffect, useState, useCallback } from 'react';
import {
  subscribeLatency,
  getLatencySummary,
  clearEntries,
  BUDGET_MS,
  type LatencyEntry,
  type LatencySummary,
  type LatencyScreen,
} from '@/lib/tick-latency';
import { Activity, X, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Web Vitals-style reporter ────────────────────────────────────────────────
// Reports tick-to-render latency as a custom metric to the browser's
// PerformanceObserver / console, matching the Web Vitals rating thresholds:
//   Good    < 100 ms
//   Needs improvement  100–200 ms
//   Poor    > 200 ms

function getRating(ms: number): 'good' | 'needs-improvement' | 'poor' {
  if (ms < 100) return 'good';
  if (ms <= BUDGET_MS) return 'needs-improvement';
  return 'poor';
}

function ratingColor(rating: ReturnType<typeof getRating>): string {
  if (rating === 'good') return 'text-emerald-400';
  if (rating === 'needs-improvement') return 'text-amber-400';
  return 'text-red-400';
}

function ratingBg(rating: ReturnType<typeof getRating>): string {
  if (rating === 'good') return 'bg-emerald-500/20 border-emerald-500/40';
  if (rating === 'needs-improvement') return 'bg-amber-500/20 border-amber-500/40';
  return 'bg-red-500/20 border-red-500/40';
}

// ─── Overlay Component ────────────────────────────────────────────────────────

interface TickLatencyOverlayProps {
  /** Only shown in development by default; pass forceShow to always render */
  forceShow?: boolean;
}

export default function TickLatencyOverlay({ forceShow }: TickLatencyOverlayProps) {
  const isDev = process.env.NODE_ENV === 'development';
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [recentEntries, setRecentEntries] = useState<LatencyEntry[]>([]);
  const [summaries, setSummaries] = useState<Record<LatencyScreen, LatencySummary | null>>({
    holdings: null,
    'eod-settlement': null,
  });

  const refreshSummaries = useCallback(() => {
    setSummaries({
      holdings: getLatencySummary('holdings'),
      'eod-settlement': getLatencySummary('eod-settlement'),
    });
  }, []);

  useEffect(() => {
    if (!isDev && !forceShow) return;
    setVisible(true);

    const unsub = subscribeLatency((entry) => {
      setRecentEntries((prev) => {
        const next = [entry, ...prev].slice(0, 8);
        return next;
      });
      refreshSummaries();

      // Web Vitals-style console reporting
      if (process.env.NODE_ENV === 'development') {
        const rating = getRating(entry.tickToRender);
        const icon = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '🔴';
        console.log(
          `%c[TickLatency] ${icon} ${entry.symbol} → ${entry.screen} | tick-to-render: ${entry.tickToRender.toFixed(1)}ms | render: ${entry.renderDuration.toFixed(1)}ms | ${rating.toUpperCase()}`,
          `color: ${rating === 'good' ? '#34d399' : rating === 'needs-improvement' ? '#fbbf24' : '#f87171'}`
        );
      }
    });

    return unsub;
  }, [isDev, forceShow, refreshSummaries]);

  if (!visible) return null;

  const screens: LatencyScreen[] = ['holdings', 'eod-settlement'];
  const screenLabel: Record<LatencyScreen, string> = {
    holdings: 'Holdings',
    'eod-settlement': 'EOD Settlement',
  };

  return (
    <div
      className="fixed bottom-20 right-3 z-50 w-72 rounded-xl border border-border/60 bg-background/95 backdrop-blur-sm shadow-2xl text-xs font-mono select-none"
      style={{ maxHeight: minimized ? 'auto' : '420px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-muted/30 rounded-t-xl">
        <div className="flex items-center gap-1.5">
          <Activity size={12} className="text-primary animate-pulse" />
          <span className="text-2xs font-semibold text-foreground tracking-wide">TICK LATENCY</span>
          <span className="text-2xs text-muted-foreground">({BUDGET_MS}ms budget)</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => { clearEntries(); setRecentEntries([]); refreshSummaries(); }}
            className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
            title="Clear measurements"
          >
            <Trash2 size={10} />
          </button>
          <button
            onClick={() => setMinimized((v) => !v)}
            className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
          >
            {minimized ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          <button
            onClick={() => setVisible(false)}
            className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={10} />
          </button>
        </div>
      </div>

      {!minimized && (
        <div className="overflow-y-auto" style={{ maxHeight: '360px' }}>
          {/* Per-screen summaries */}
          <div className="p-2 space-y-2">
            {screens.map((screen) => {
              const s = summaries[screen];
              if (!s || s.count === 0) {
                return (
                  <div key={screen} className="rounded-lg border border-border/40 px-2.5 py-2 bg-muted/10">
                    <p className="text-2xs font-semibold text-muted-foreground">{screenLabel[screen]}</p>
                    <p className="text-2xs text-muted-foreground/60 mt-0.5">No measurements yet</p>
                  </div>
                );
              }
              const p95Rating = getRating(s.p95);
              return (
                <div key={screen} className={`rounded-lg border px-2.5 py-2 ${ratingBg(p95Rating)}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-2xs font-semibold text-foreground">{screenLabel[screen]}</p>
                    <span className={`text-2xs font-bold ${ratingColor(p95Rating)}`}>
                      p95: {s.p95}ms
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-1 text-2xs">
                    <div>
                      <p className="text-muted-foreground">p50</p>
                      <p className={`font-semibold ${ratingColor(getRating(s.p50))}`}>{s.p50}ms</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">p75</p>
                      <p className={`font-semibold ${ratingColor(getRating(s.p75))}`}>{s.p75}ms</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">p99</p>
                      <p className={`font-semibold ${ratingColor(getRating(s.p99))}`}>{s.p99}ms</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">max</p>
                      <p className={`font-semibold ${ratingColor(getRating(s.max))}`}>{s.max}ms</p>
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <p className="text-muted-foreground">{s.count} samples</p>
                    {s.overBudgetCount > 0 && (
                      <p className="text-red-400 font-semibold">
                        {s.overBudgetCount} over budget ({s.overBudgetPct}%)
                      </p>
                    )}
                    {s.overBudgetCount === 0 && s.count > 0 && (
                      <p className="text-emerald-400 font-semibold">All within budget ✓</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recent tick log */}
          {recentEntries.length > 0 && (
            <div className="border-t border-border/40 px-2 pb-2">
              <p className="text-2xs text-muted-foreground font-semibold py-1.5 px-0.5">Recent ticks</p>
              <div className="space-y-0.5">
                {recentEntries.map((e, i) => {
                  const rating = getRating(e.tickToRender);
                  return (
                    <div key={i} className="flex items-center justify-between py-0.5 px-1.5 rounded hover:bg-muted/20">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          rating === 'good' ? 'bg-emerald-400' :
                          rating === 'needs-improvement' ? 'bg-amber-400' : 'bg-red-400'
                        }`} />
                        <span className="text-foreground truncate">{e.symbol}</span>
                        <span className="text-muted-foreground/60 text-2xs truncate">{screenLabel[e.screen]}</span>
                      </div>
                      <span className={`font-semibold flex-shrink-0 ml-1 ${ratingColor(rating)}`}>
                        {e.tickToRender.toFixed(0)}ms
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="border-t border-border/40 px-3 py-1.5 flex items-center gap-3">
            <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className="text-muted-foreground">&lt;100ms</span></div>
            <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /><span className="text-muted-foreground">100–200ms</span></div>
            <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400" /><span className="text-muted-foreground">&gt;200ms</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
